type Props = {
  size?: number;
  className?: string;
  /**
   * Force the control's colors to a resolved theme instead of using the
   * surrounding app's overlay tokens. Useful inside previews that deliberately
   * simulate a different theme.
   */
  forceTheme?: "light" | "dark";
};

// Web mirror of IdleBubbleView in GlobalDictationOverlayController.java.
// Both must stay visually identical so the home pill, onboarding, and the
// floating overlay read as a single product mark.
//
// This uses the same inverting glass material as the Electron pill: dark glass
// over a light app, light glass over a dark app.
const DictationBubbleIcon = ({
  size = 56,
  className = "",
  forceTheme,
}: Props) => {
  const radius = Math.round(size * (18 / 56));
  const fontSize = Math.round(size * 0.34);
  const resolvedMaterial =
    forceTheme === "dark"
      ? {
          background: "rgba(246, 248, 252, 0.86)",
          color: "#10172b",
          edge: "inset 0 1.2px 0.6px rgba(255,255,255,0.95), inset 0 -1.4px 1.6px rgba(24,43,101,0.12)",
        }
      : forceTheme === "light"
        ? {
            background: "rgba(28, 31, 38, 0.82)",
            color: "#f6f8fb",
            edge: "inset 0 1.2px 0.6px rgba(255,255,255,0.22), inset 0 -1.4px 1.6px rgba(0,0,0,0.30)",
          }
        : undefined;

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        color: resolvedMaterial?.color ?? "var(--overlay-ink)",
        background: resolvedMaterial?.background ?? "var(--overlay-glass)",
        fontSize,
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: "-0.08em",
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        boxShadow:
          resolvedMaterial === undefined
            ? "var(--overlay-edge), var(--overlay-lift)"
            : `${resolvedMaterial.edge}, 0 2px 6px rgba(0,0,0,0.28), 0 18px 44px -14px rgba(0,0,0,0.55)`,
        backdropFilter: "blur(26px) saturate(190%)",
        WebkitBackdropFilter: "blur(26px) saturate(190%)",
      }}
    >
      {/* Same restrained diagonal lift used by the desktop shell. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(157deg, rgba(255,255,255,0.16), transparent 52%)",
        }}
      />
      <span className="relative -translate-x-[0.03em]">1T</span>
    </span>
  );
};

export default DictationBubbleIcon;
